import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clapperboard } from "lucide-react";
import { useMyCinema } from "../context/MyCinemaContext";
import { useTranslate } from "../context/TranslateContext";

export const AuthModal = () => {
  const { authOpen, setAuthOpen, login, register } = useMyCinema();
  const { t } = useTranslate();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isReg = mode === "register";

  const submit = async () => {
    setErr("");
    if (!email.trim() || !password) { setErr("Please enter your email and password."); return; }
    if (isReg && !name.trim()) { setErr("Please enter your name."); return; }
    if (isReg && password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      if (isReg) await register(email.trim(), password, name.trim());
      else await login(email.trim(), password);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setErr(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {authOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setAuthOpen(false)} data-testid="auth-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md overflow-hidden rounded-3xl border border-edge bg-gradient-to-br from-paper to-surface p-8 shadow-2xl shadow-black/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clapperboard className="h-6 w-6 text-gold" />
                <span className="font-display text-2xl text-cream">{t(isReg ? "Create your account" : "Welcome back")}</span>
              </div>
              <button onClick={() => setAuthOpen(false)} data-testid="auth-close" className="text-ash hover:text-cream"><X className="h-5 w-5" /></button>
            </div>

            {/* Mode tabs */}
            <div className="mt-5 flex rounded-full border border-edge p-1 font-body text-sm">
              <button data-testid="auth-tab-login" onClick={() => { setMode("login"); setErr(""); }}
                className={`flex-1 rounded-full py-2 transition-colors ${!isReg ? "bg-gold text-ink" : "text-ash hover:text-cream"}`}>
                {t("Log in")}
              </button>
              <button data-testid="auth-tab-register" onClick={() => { setMode("register"); setErr(""); }}
                className={`flex-1 rounded-full py-2 transition-colors ${isReg ? "bg-gold text-ink" : "text-ash hover:text-cream"}`}>
                {t("Sign up")}
              </button>
            </div>

            <div className="mt-5 space-y-4">
              {isReg && (
                <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Your name")}
                  className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
              )}
              <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("Email")}
                className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
              <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Password")}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                className="w-full rounded-xl border border-edge bg-surface px-4 py-3 font-body text-cream placeholder:text-ash focus:border-gold focus:outline-none" />
              {err && <div className="font-body text-sm text-crimson" data-testid="auth-error">{t(err)}</div>}
              <button data-testid="auth-submit" onClick={submit} disabled={busy}
                className="w-full rounded-full bg-gold py-3 font-body font-medium text-ink transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60">
                {busy ? t("Please wait…") : t(isReg ? "Create account" : "Log in")}
              </button>

              <div className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-edge" />
                <span className="font-body text-xs uppercase tracking-widest text-ash">{t("or")}</span>
                <div className="h-px flex-1 bg-edge" />
              </div>
              <button data-testid="auth-socialpulse" disabled title={t("Coming soon")}
                className="w-full cursor-not-allowed rounded-full border border-edge py-3 font-body text-ash opacity-70">
                {t("Continue with SocialPulse")} · {t("coming soon")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
